import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { authAPI } from './services/api';
import Header from './components/Header';
import LoadingSpinner from './components/LoadingSpinner';
import LoginPage from './pages/LoginPage';
import SetPasswordPage from './pages/SetPassword';
import ResetPasswordPage from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import AdminDashboard from './pages/AdminDashboard';
import QuotePage from './pages/QuotePage';
import DemoPage from './pages/DemoPage';
import TVsPage from './pages/TVsPage';
import DDCInfo from './pages/DDCInfo';
import ScanPage from './pages/ScanPage';
import ReferralProgram from './pages/ReferralProgram';
import './index.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  // Check if user is already logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      // Skip auth check if already authenticated or on public pages
      const publicRoutes = ['/login', '/quote', '/qrcode', '/referralprogram', '/scan', '/tvs', '/ddcinfo', '/set-password', '/reset-password'];
      if (authenticated || publicRoutes.includes(window.location.pathname)) {
        setLoading(false);
        return;
      }

      try {
        const response = await authAPI.getCurrentUser();
        setUser(response.user);
        setAuthenticated(true);
        if (response.must_set_password && window.location.pathname !== '/set-password') {
          window.location.replace('/set-password');
          return;
        }
      } catch (error) {
        // User not authenticated, which is fine
        setAuthenticated(false);
        setUser(null);
        
        // Log mobile authentication failures for debugging
        const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        if (isMobile && error.response?.status === 401) {
          console.warn('Mobile authentication check failed:', error);
          // Could add toast notification or mobile-specific handling here
        }
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  const handleLogin = (userData) => {
    setUser(userData);
    setAuthenticated(true);
  };

  const handleLogout = () => {
    setUser(null);
    setAuthenticated(false);
  };

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-gradient-dental rounded-full flex items-center justify-center mb-4 shadow-dental">
            <span className="text-2xl">🦷</span>
          </div>
          <LoadingSpinner size="lg" className="text-primary-600 mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App min-h-screen bg-gray-50">
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              style: {
                background: '#10b981',
              },
            },
            error: {
              style: {
                background: '#ef4444',
              },
            },
          }}
        />
        
        {authenticated && user && (
          <Header user={user} onLogout={handleLogout} />
        )}
        
        <main className={authenticated && user ? '' : ''}>
          <Routes>
            <Route 
              path="/login" 
              element={
                authenticated ? (
                  <Navigate to="/" replace />
                ) : (
                  <LoginPage onLogin={handleLogin} />
                )
              } 
            />
            
            <Route 
              path="/" 
              element={
                authenticated && user ? (
                  user.is_admin ? (
                    <AdminDashboard user={user} />
                  ) : (
                    <Dashboard user={user} />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              } 
            />
            
            <Route 
              path="/admin" 
              element={
                authenticated && user && user.is_admin ? (
                  <AdminDashboard user={user} />
                ) : (
                  <Navigate to="/" replace />
                )
              } 
            />

            {/* Public quote page */}
            <Route 
              path="/quote" 
              element={<QuotePage />} 
            />
            
            {/* QR Code page */}
            <Route 
              path="/qrcode" 
              element={<DemoPage />} 
            />
            {/* Set Password page */}
            <Route
              path="/set-password"
              element={<SetPasswordPage />}
            />
            {/* Reset Password page */}
            <Route
              path="/reset-password"
              element={<ResetPasswordPage />}
            />

            {/* Referral Program iPad page */}
            <Route
              path="/referralprogram"
              element={<ReferralProgram />}
            />

            {/* In-page Scanner (beeps only on detection) */}
            <Route
              path="/scan"
              element={<ScanPage />}
            />

            {/* TVs page (public) */}
            <Route
              path="/tvs"
              element={<TVsPage />}
            />

            {/* DDC info page (public) */}
            <Route
              path="/ddcinfo"
              element={<DDCInfo />}
            />
            
            {/* Catch all route */}
            <Route 
              path="*" 
              element={<Navigate to="/" replace />} 
            />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

export default App;
